import React, { useState } from 'react';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';

const Manual = () => {
  useGoogleAnalytics();
  const [activeTab, setActiveTab] = useState('scoreboard');
  return (
    <div className="min-h-screen flex flex-col">
      <HeaderSection />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-8">
            <nav className="flex space-x-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('scoreboard')}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === 'scoreboard'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Scoreboard
              </button>
              <button
                onClick={() => setActiveTab('entertainment')}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === 'entertainment'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Entertainment
              </button>
            </nav>
          </div>
          <h1 className="text-4xl font-bold mb-8">{activeTab === 'scoreboard' ? 'Scoreboard Manual' : 'Entertainment Manual'}</h1>
          
          {activeTab === 'scoreboard' ? (
            <>
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Overview</h2>
            <p className="mb-4">
              The rink hockey scoreboard is a comprehensive digital scoring system designed for rink hockey matches. 
              It features a main control panel and a separate live view screen that can be displayed on a secondary monitor.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Period Management</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">Regular Periods</h3>
              <p>
                A rink hockey match consists of two regular periods by default. Each period runs for the configured game duration
                (set in settings). The scoreboard automatically manages period transitions and break periods.
              </p>
              
              <h3 className="text-xl font-medium">Period Features</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Automatic period counting (1-4)</li>
                <li>Configurable period duration for regular periods</li>
                <li>Visual period indicator</li>
                <li>Automatic break period initiation when a period ends</li>
                <li>Period controls (increment/decrement) for manual adjustments</li>
              </ul>

              <h3 className="text-xl font-medium">Overtime Periods</h3>
              <p>
                If the match requires overtime, the scoreboard supports up to two overtime periods (periods 3 and 4).
                Overtime periods have their own configurable duration, which can be different from regular period duration.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <h4 className="font-medium mb-2 text-blue-700">Automatic Overtime Logic:</h4>
                <ul className="list-disc pl-6 space-y-1 text-blue-700">
                  <li>After period 1 ends: Always advances to period 2</li>
                  <li>After period 2 ends: Only advances to period 3 (overtime) if the score is tied</li>
                  <li>After period 3 ends: Only advances to period 4 (second overtime) if the score is still tied</li>
                  <li>If a team is leading after period 2 or 3, the game ends without advancing to overtime</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Team Fouls Management</h2>
            <div className="space-y-4">
              <p>
                The scoreboard tracks team fouls for both teams, with special handling for the 10th and subsequent fouls.
              </p>
              
              <h3 className="text-xl font-medium">Foul Counter Features</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Individual foul counters for each team</li>
                <li>Visual indicators for foul counts:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Normal display for fouls 1-9</li>
                    <li>Red highlight for 10th foul and beyond</li>
                    <li>Large, clear numbers for easy visibility</li>
                  </ul>
                </li>
                <li>Controls for foul management:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Increment button to add fouls</li>
                    <li>Decrement button to correct mistakes</li>
                    <li>Quick reset option available</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Important Rules:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>10th team foul triggers special visual indicator</li>
                  <li>Foul count persists through all periods</li>
                  <li>Foul counters can be adjusted at any time if needed</li>
                  <li>Both live view and control panel show current foul counts</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Personal Fouls and Penalties</h2>
            <div className="space-y-4">
              <p>
                The scoreboard includes a comprehensive system for tracking individual player penalties and their durations.
              </p>
              
              <h3 className="text-xl font-medium">Penalty Management Features</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Individual penalty tracking per player:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Player number identification</li>
                    <li>Multiple concurrent penalties support</li>
                    <li>Automatic penalty time tracking</li>
                  </ul>
                </li>
                <li>Penalty durations:
                  <ul className="list-disc pl-6 mt-2">
                    <li>2-minute minor penalties</li>
                    <li>5-minute major penalties</li>
                    <li>10-minute misconduct penalties</li>
                    <li>Custom duration penalties for special cases</li>
                  </ul>
                </li>
                <li>Visual indicators:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Countdown timer for each active penalty</li>
                    <li>Clear display of player number and remaining time</li>
                    <li>Automatic removal when penalty expires</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Penalty Management Tips:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Penalties can be added at any time during the game</li>
                  <li>Multiple penalties for the same player stack in sequence</li>
                  <li>Penalty timers pause when game timer is stopped</li>
                  <li>Manual removal option available if needed</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Buzzer System</h2>
            <div className="space-y-4">
              <p>
                The scoreboard includes an automatic buzzer system that sounds in the following situations:
              </p>
              
              <h3 className="text-xl font-medium">When the Buzzer Sounds</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>When the game timer reaches 00:00</li>
                <li>When a timeout is requested and the game timer is paused</li>
                <li>When the timeout reaches 15 seconds before end and it's end</li>
                <li>When the break timer reaches 00:00</li>
              </ul>

              <p>
                The buzzer helps officials and players stay synchronized with game events and
                ensures clear communication of important moments during the match.
              </p>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Period Timing Logic:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Periods 1-2: Use regular game duration (e.g., 25:00)</li>
                  <li>Periods 3-4: Use overtime duration (e.g., 10:00)</li>
                  <li>Each period starts with its full configured duration</li>
                  <li>Timer counts down to 00:00</li>
                  <li>Break period automatically starts when timer reaches 00:00</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Break Periods</h2>
            <div className="space-y-4">
              <p>
                Break periods occur automatically between game periods. During breaks:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>A break overlay appears on the live screen</li>
                <li>Break timer counts down the configured break duration</li>
                <li>Optional image carousel displays on the live screen</li>
                <li>Clear indication of the upcoming period</li>
                <li>Break can be skipped if needed</li>
              </ul>
              
              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Automatic Break Flow:</h4>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>When a period timer reaches 00:00, the buzzer sounds</li>
                  <li>The system automatically advances to the next period</li>
                  <li>The break overlay starts with the appropriate duration:
                    <ul className="list-disc pl-6 mt-1">
                      <li>Period 1 → 2: Standard break duration (default: 10 minutes)</li>
                      <li>Period 2 → 3: Overtime break 1 duration (default: 3 minutes)</li>
                      <li>Period 3 → 4: Overtime break 2 duration (default: 2 minutes)</li>
                    </ul>
                  </li>
                  <li>When the break timer reaches 00:00, the buzzer sounds again</li>
                  <li>The break overlay closes and the next period is ready to start</li>
                </ol>
                <p className="mt-2 text-sm text-gray-600">
                  <strong>Note:</strong> All break durations can be configured in the settings. 
                  Breaks only occur when advancing to the next period (based on overtime logic).
                </p>
              </div>
            </div>
          </section>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Timeout Management</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">Timeout System</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Each team has one timeout per match</li>
                <li>Two-step timeout process:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Step 1: Request timeout (yellow indicator)</li>
                    <li>Step 2: Start timeout when ready (activates the timer)</li>
                  </ul>
                </li>
                <li>60-second countdown timer with clear visual overlay</li>
                <li>Visual warning at 15 seconds (timer turns red)</li>
                <li>Audio buzzer sounds at 15 seconds remaining</li>
                <li>Timeout requests can be cancelled before activation</li>
                <li>Active timeouts show on both control panel and live view</li>
                <li>Clear visual and audio signals when timeout ends</li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Timeout Process:</h4>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>Click the timeout button for the desired team (requests timeout)</li>
                  <li>Yellow indicator shows pending timeout request</li>
                  <li>Click "Start Timeout" to begin the 60-second countdown</li>
                  <li>Timeout overlay appears with countdown timer</li>
                  <li>System automatically ends timeout after 60 seconds</li>
                </ol>
              </div>
            </div>
          </section>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Live Screen Functionality</h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium">Dual Screen Setup</h3>
              <p>
                The scoreboard supports a dual-screen setup where the control panel remains on your primary screen while
                the live view is automatically positioned on your secondary display. The live view can be toggled to 
                fullscreen mode for optimal visibility.
              </p>
              
              <h3 className="text-xl font-medium">Display Elements</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Team names and scores</li>
                <li>Game timer with period indication</li>
                <li>Team fouls counter</li>
                <li>Active penalties with countdown timers</li>
                <li>Timeout overlays</li>
                <li>Break period overlays</li>
                <li>Custom image carousel during breaks</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Configurable Settings</h2>
            <div className="space-y-4">
              <p>
                The following settings can be configured through the settings panel:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Game Duration:</strong> Set the default period length for regular periods (format: MM:SS)</li>
                <li><strong>Overtime Duration:</strong> Configure the length of overtime periods (format: MM:SS)</li>
                <li><strong>Break Duration (P1→P2):</strong> Set the length of break between period 1 and 2 (format: MM:SS)</li>
                <li><strong>Break Duration (P2→P3):</strong> Set the length of break between period 2 and 3 (format: MM:SS)</li>
                <li><strong>Break Duration (P3→P4):</strong> Set the length of break between period 3 and 4 (format: MM:SS)</li>
                <li><strong>Background Color:</strong> Customize the scoreboard background color</li>
                <li><strong>Carousel Images:</strong> Upload up to 5 images to display during breaks (automatically optimized)</li>
              </ul>
              
              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Image Guidelines:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Maximum of 5 images</li>
                  <li>Images are automatically compressed and resized</li>
                  <li>Maximum dimensions: 800x600 pixels</li>
                  <li>Supported format: JPEG</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Storage</h2>
            <div className="space-y-4">
              <p>
                The scoreboard uses your browser's local storage to save and persist the game state. This means:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All game data is stored locally in your browser</li>
                <li>Data persists even if you close and reopen the browser</li>
                <li>No internet connection required after initial page load</li>
                <li>Data is private and stays on your device</li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Stored Information:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Team names and scores</li>
                  <li>Current period and time</li>
                  <li>Active penalties</li>
                  <li>Game settings (period durations, break duration)</li>
                  <li>Custom background color</li>
                  <li>Carousel images (as compressed base64 data)</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                <p className="text-yellow-700">
                  <strong>Note:</strong> Clearing your browser's data or using private/incognito mode will reset all scoreboard settings and game state.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Keyboard Shortcuts</h2>
            <div className="space-y-4">
              <p>
                The scoreboard supports various keyboard shortcuts for quick control:
              </p>
              
              <h3 className="text-xl font-medium">Game Control</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>S Key:</strong> Start/Stop the game timer (only works when not typing in an input field)</li>
              </ul>

            </div>
          </section>



            </>
          ) : (
            <>
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Entertainment System</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-700">
                  <strong>License Required:</strong> The entertainment system features require a valid license key to activate. 
                  Contact your system administrator or the software provider to obtain a license.
                </p>
              </div>

              <h3 className="text-xl font-medium">YouTube Music Player</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Stream music from YouTube during breaks and pre-game</li>
                <li>Support for both single videos and playlists</li>
                <li>Volume control independent from game sounds</li>
                <li>Playback controls (play/pause)</li>
                <li>Music automatically pauses when game starts</li>
                <li>Configure playlist URL in settings</li>
              </ul>

              <h3 className="text-xl font-medium mt-4">In-Game Music System</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Upload and manage up to 10 music tracks</li>
                <li>Supported formats: MP3, WAV</li>
                <li>Maximum file size: 10MB per track</li>
                <li>Keyboard shortcuts:
                  <ul className="list-disc pl-6 mt-2">
                    <li><strong>Ctrl + 1-0 Keys:</strong> Play/stop corresponding track</li>
                    <li><strong>Press Crtl twice:</strong>Show the In-Game overlay</li>
                  </ul>
                </li>
                <li>Perfect for quick sound effects or celebration music</li>
                <li>The music automatically stops when the game resumes</li>
                <li>Individual volume control for each track</li>
                <li>Tracks persist between sessions</li>
              </ul>

              <h3 className="text-xl font-medium mt-4">Pre-Game Video System</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Display videos before matches or during breaks</li>
                <li>Support for up to 4 different videos</li>
                <li>Compatible with YouTube videos and playlists</li>
                <li>Features:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Full-screen mode support</li>
                    <li>Loop option for continuous playback</li>
                    <li>Independent volume control</li>
                    <li>Automatic playback options</li>
                  </ul>
                </li>
                <li>Perfect for:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Sponsor advertisements</li>
                    <li>Team introductions</li>
                    <li>League announcements</li>
                    <li>Entertainment during breaks</li>
                  </ul>
                </li>
              </ul>

              <div className="bg-gray-100 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Entertainment System Tips:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Configure all media before the match starts</li>
                  <li>Test sound levels during setup</li>
                  <li>Ensure stable internet connection for YouTube features</li>
                  <li>Use keyboard shortcuts for quick music control during the game</li>
                  <li>Videos and music automatically stop when game timer starts</li>
                </ul>
              </div>
            </div>
          </section>


            </>
          )}
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default Manual;
